import { Info } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";
import { useWatch } from "react-hook-form";
import { z } from "zod/v3";

import { Button } from "~/components/ui/button";
import { Combobox, type ComboboxItem } from "~/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "~/components/ui/input-group";

export const taxCategorySchema = z
  .object({
    name: z
      .string()
      .min(1, "Name is required")
      .max(100, "Name must be less than 100 characters"),
    taxMethod: z.enum(["automatic", "manual"]),
    value: z.string().optional(),
    taxCode: z.string().optional().nullable(),
  })
  .refine(
    (data) => {
      if (data.taxMethod === "manual") {
        if (!data.value || data.value.trim() === "") {
          return false;
        }
        const num = Number.parseFloat(data.value);
        return !Number.isNaN(num) && num >= 0 && num <= 100;
      }
      return !!data.taxCode && data.taxCode.trim() !== "";
    },
    {
      message:
        "Please provide a tax rate (manual) or select a Stripe tax code (automatic)",
      path: ["value"],
    }
  );

export type TaxCategoryFormData = z.infer<typeof taxCategorySchema>;

export const EMPTY_TAX_CATEGORY_FORM_VALUES: TaxCategoryFormData = {
  name: "",
  taxMethod: "manual",
  value: "",
  taxCode: "",
};

interface TaxCategoryFormModalProps {
  description: string;
  form: UseFormReturn<TaxCategoryFormData>;
  isLoadingTaxCodes: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (values: TaxCategoryFormData) => void | Promise<void>;
  submitLabel: string;
  taxCodeOptions: ComboboxItem<string>[];
  title: string;
}

export const TaxCategoryFormModal = ({
  description,
  form,
  isLoadingTaxCodes,
  isSubmitting,
  onClose,
  onSubmit,
  submitLabel,
  taxCodeOptions,
  title,
}: TaxCategoryFormModalProps) => {
  const taxMethod = useWatch({ control: form.control, name: "taxMethod" });

  return (
    <Dialog onOpenChange={onClose} open>
      <DialogContent
        className="w-full max-w-120"
        noCloseButton
        noScrollPrevention
      >
        <DialogHeader className="items-center space-y-2">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="body">{description}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={isSubmitting}
                      placeholder="Food, Beverages, Alcohol..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="taxMethod"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tax Calculation Method</FormLabel>
                  <FormControl>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        className={`rounded-lg border p-3 text-left transition-colors ${
                          field.value === "manual"
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-muted-foreground/50"
                        }`}
                        disabled={isSubmitting}
                        onClick={() => field.onChange("manual")}
                        type="button"
                      >
                        <div className="text-sm font-medium">Manual Rate</div>
                        <div className="text-muted-foreground mt-0.5 text-xs">
                          Set a fixed tax percentage
                        </div>
                      </button>
                      <button
                        className={`rounded-lg border p-3 text-left transition-colors ${
                          field.value === "automatic"
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-muted-foreground/50"
                        }`}
                        disabled={isSubmitting}
                        onClick={() => field.onChange("automatic")}
                        type="button"
                      >
                        <div className="text-sm font-medium">
                          Automatic (Stripe)
                        </div>
                        <div className="text-muted-foreground mt-0.5 text-xs">
                          Based on location
                        </div>
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {taxMethod === "manual" ? (
              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tax Rate</FormLabel>
                    <FormControl>
                      <InputGroup className="h-9 rounded-full">
                        <InputGroupInput
                          {...field}
                          className="body-sm px-3 py-2"
                          disabled={isSubmitting}
                          inputMode="decimal"
                          onChange={(event) => {
                            const value = event.target.value.replace(
                              /[^\d.]/g,
                              ""
                            );
                            const parts = value.split(".");
                            if (parts.length > 2) {
                              return;
                            }
                            if (parts[0] && parts[0].length > 2) {
                              return;
                            }
                            if (parts[1] && parts[1].length > 3) {
                              return;
                            }
                            field.onChange(value);
                          }}
                          placeholder="0"
                          type="text"
                          value={field.value ?? ""}
                        />
                        <InputGroupAddon align="inline-end">
                          <InputGroupText>%</InputGroupText>
                        </InputGroupAddon>
                      </InputGroup>
                    </FormControl>
                    <FormDescription>
                      This rate will apply to all locations using this tax
                      category.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}

            {taxMethod === "automatic" ? (
              <>
                <FormField
                  control={form.control}
                  name="taxCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Stripe Tax Code</FormLabel>
                      <FormControl>
                        <Combobox
                          disabled={isSubmitting || isLoadingTaxCodes}
                          dropdownClassName="w-80"
                          filter={(value, search) => {
                            const item = taxCodeOptions.find(
                              (option) => option.value === value
                            );
                            if (!item) {
                              return 0;
                            }
                            const searchLower = search.toLowerCase();
                            if (
                              item.label.toLowerCase().includes(searchLower)
                            ) {
                              return 1;
                            }
                            if (
                              item.keywords?.some((keyword) =>
                                keyword.includes(searchLower)
                              )
                            ) {
                              return 1;
                            }
                            return 0;
                          }}
                          items={taxCodeOptions}
                          onValueChange={(value) => field.onChange(value ?? "")}
                          placeholder="Search tax codes..."
                          value={field.value ?? null}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="border-info/30 bg-info/10 dark:border-info/30 dark:bg-info/10 flex gap-3 rounded-lg border p-3">
                  <Info className="text-info dark:text-info mt-0.5 size-5 shrink-0" />
                  <div className="text-sm">
                    <p className="text-info dark:text-info/80 font-medium">
                      How automatic tax works
                    </p>
                    <p className="text-info dark:text-info/70 mt-1">
                      Stripe calculates the correct tax rate based on each
                      location. If you have locations in multiple states, each
                      will automatically use the appropriate rate for that
                      jurisdiction.
                    </p>
                  </div>
                </div>
              </>
            ) : null}

            <div className="border-warning/30 bg-warning/10 dark:border-warning/30 dark:bg-warning/10 rounded-lg border p-3">
              <p className="text-warning dark:text-warning/80 text-xs">
                <span className="font-medium">Tax Compliance Notice:</span> You
                are responsible for ensuring your tax rates are accurate and
                comply with local regulations. Plasma does not provide tax
                advice and is not liable for any tax calculation errors or
                compliance issues.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={onClose}
                size="lg"
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button disabled={isSubmitting} size="lg" type="submit">
                {submitLabel}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
